import { useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import {
  isNoResponse,
  isRetryableHttpError,
  readStatusCode,
} from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  ApiError,
  AuthCodeRequest,
  AuthCodeResponse,
  UseAuthCodeMutationOptions,
} from '../useSignupMutation/types';

const requestAuthCode = async (
  requestData: AuthCodeRequest,
): Promise<AuthCodeResponse> => {
  try {
    const { data } = await apiClient.post<AuthCodeResponse>(
      '/v1/auth-code',
      requestData,
    );
    return data;
  } catch (error: any) {
    // A 4xx is the answer to what the user typed (unknown email, too many
    // attempts), which the sign-in screens show.
    const status = readStatusCode(error);
    const isUserOutcome = status !== undefined && status < 500;
    const log =
      isNoResponse(error) || isUserOutcome ? Logger.warn : Logger.error;
    log('Error requesting auth code', { error });
    const isAuthError = error?.code === 'AUTH_CODE_ERROR';

    const apiError: ApiError = {
      message: error?.message || 'Failed to send verification code',
      code: isAuthError ? 'AUTH_CODE_ERROR' : error?.code || 'USER_NOT_FOUND',
      details: error?.details,
      statusCode: status,
    };
    throw apiError;
  }
};

export const useAuthCodeMutation = (options?: UseAuthCodeMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['auth', 'authCode'],
    mutationFn: requestAuthCode,
    onMutate: async (newAuthCodeData) => {
      await queryClient.cancelQueries({ queryKey: ['authCode'] });
      const previousAuthCodeData = queryClient.getQueryData(['authCode']);
      queryClient.setQueryData(['authCode'], (old: any) => ({
        ...old,
        ...newAuthCodeData,
      }));
      return { previousAuthCodeData };
    },
    onError: (_, __, context) => {
      if (context?.previousAuthCodeData) {
        queryClient.setQueryData(['authCode'], context.previousAuthCodeData);
      }
    },
    retry: (failureCount, error) =>
      failureCount < 2 && isRetryableHttpError(error),
    ...options,
  });
};
